/// <reference path="SaveQueueHelper.ts" />

class SaveQueueViewController extends MUIViewController
{
    static newInstance() : SaveQueueViewController {
        let vc = new SaveQueueViewController("save-queue-view");
        vc.initWithResource("layout/save_queue/SaveQueueView.html");
        return vc;
    }

    get preferredContentSize() { return new MIOSize(520, 560); }

    private retryButton:MUIButton = null;
    private emptyLabel:MUILabel = null;
    private tableView:UITableView = null;

    private items:SaveQueueItem[] = [];

    viewDidLoad(){
        super.viewDidLoad();

        this.retryButton = MUIOutlet(this, "retry-btn", "MUIButton");
        this.retryButton.setAction(this, function(this:SaveQueueViewController){
            SaveQueueHelper.sharedInstance().retry();
            this.dismissViewController(true);
        });

        this.emptyLabel = MUIOutlet(this, "empty-lbl", "MUILabel");

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;

        MIONotificationCenter.defaultCenter().addObserver(this, SaveQueueDidChange, function(this:SaveQueueViewController){
            this.reloadItems();
        });
    }

    viewWillAppear(animated?:boolean){
        super.viewWillAppear(animated);
        this.reloadItems();
    }

    viewWillDisappear(animated?:boolean){
        super.viewWillDisappear(animated);
        MIONotificationCenter.defaultCenter().removeObserver(this, SaveQueueDidChange);
    }

    private reloadItems(){
        SaveQueueHelper.sharedInstance().fetchPendingItems((items:SaveQueueItem[]) => {
            this.items = items;
            this.emptyLabel.hidden = items.length > 0;
            this.retryButton.hidden = items.length == 0;
            this.tableView.reloadData();
        });
    }

    numberOfSections(tableView:UITableView) {
        return 1;
    }

    numberOfRowsInSection(tableView:UITableView, section:number) {
        return this.items.length;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let cell = tableView.dequeueReusableCellWithIdentifier("SaveQueueCell") as SaveQueueCell;
        cell.delegate = this;
        cell.position = indexPath.row + 1;
        cell.item = this.items[indexPath.row];
        return cell;
    }

    saveQueueCellDidPressDelete(cell:SaveQueueCell, item:SaveQueueItem){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("DELETE_PENDING_CHANGE", "Delete the pending change?"), MIOLocalizeString("DELETE_PENDING_CHANGE_MESSAGE", "Its changes will not reach the server and the rest of the pending changes will be sent. Reload the page afterwards to see the data of the server."), MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("DELETE", "Delete"), MUIAlertActionStyle.Destructive, this, function(this:SaveQueueViewController){
            SaveQueueHelper.sharedInstance().deleteItem(item, () => { this.reloadItems(); });
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "Cancel"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }
}

class SaveQueueCell extends UITableViewCell
{
    delegate:SaveQueueViewController = null;
    position = 0;

    private titleLabel:MUILabel = null;
    private dateLabel:MUILabel = null;
    private entitiesLabel:MUILabel = null;
    private statusLabel:MUILabel = null;
    private deleteButton:MUIButton = null;

    private _item:SaveQueueItem = null;

    awakeFromHTML(){
        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");
        this.dateLabel = MUIOutlet(this, "date-lbl", "MUILabel");
        this.entitiesLabel = MUIOutlet(this, "entities-lbl", "MUILabel");
        this.statusLabel = MUIOutlet(this, "status-lbl", "MUILabel");

        this.deleteButton = MUIOutlet(this, "delete-btn", "MUIButton");
        this.deleteButton.setAction(this, function(this:SaveQueueCell){
            this.delegate?.saveQueueCellDidPressDelete(this, this._item);
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    set item(item:SaveQueueItem){
        this._item = item;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let count = item.entities.length;
        this.titleLabel.text = "#" + this.position + " · " + count + " " + MIOLocalizeString(count == 1 ? "CHANGE" : "CHANGES", count == 1 ? "change" : "changes");
        this.dateLabel.text = item.date != null ? ad.longDateTimeFormatter.stringFromDate(item.date) : "";
        this.entitiesLabel.text = SaveQueueCell.summaryForEntities(item.entities);

        if (item.failed) {
            let status = MIOLocalizeString("SERVER_REJECTED_IT", "The server rejected it");
            if (item.errorCode != null) status += " (" + (item.errorCode == 0 ? MIOLocalizeString("NO_CONNECTION", "No connection") : item.errorCode) + ")";
            if (item.errorMessage?.length > 0) status += ": " + item.errorMessage;
            this.statusLabel.text = status;
        }
        else {
            this.statusLabel.text = MIOLocalizeString("WAITING_TO_BE_SENT", "Waiting to be sent");
        }

        MUICoreLayerRemoveStyle(this.layer, "failed");
        if (item.failed) MUICoreLayerAddStyle(this.layer, "failed");
        this.deleteButton.hidden = !item.failed;
    }

    // "Insert PurchaseInvoice, Update Product ×2 …" from the lines of the block ({action, entity: "Name://id", values})
    static summaryForEntities(entities:any[]) : string {
        let groups:{[key:string]:number} = {};
        let order:string[] = [];

        for (let e of entities) {
            let action = (e["action"] ?? "").toString().toLowerCase();
            action = action.charAt(0).toUpperCase() + action.substring(1);
            let name = (e["entity"] ?? "").toString().split("://")[0];
            let key = action + " " + name;
            if (groups[key] == null) { groups[key] = 0; order.push(key); }
            groups[key]++;
        }

        let parts = order.map(key => groups[key] > 1 ? key + " ×" + groups[key] : key);
        if (parts.length > 4) return parts.slice(0, 4).join(", ") + " … +" + (parts.length - 4);
        return parts.join(", ");
    }
}
